import React, { Fragment, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { InsuranceData, InsuranceSelect } from 'common/components/InsuranceSelect/InsuranceSelect';
import { COUNTRY } from 'common/constants';
import { FieldValidation } from 'common/Validator/Validator';
import { selectDataClinicCountry, selectInsuranceListForCountry } from 'connect/selectors';
import { ErrorMessage } from 'pages/checkout/components/common/input-styled-components';
import { LabeledInput } from 'pages/checkout/components/common/LabeledInput/LabeledInput';
import { CheckoutInsurancePlan, CheckoutInsuranceProvider } from 'pages/checkout/connect/types';

import Logger from 'utils/Logger';

import { SelectWrapper } from './InsurancePicker-styled-components';

interface ValidationState {
  allValid?: boolean,
  insuranceProvider?: FieldValidation,
  insurancePlan?: FieldValidation,
}

interface InsurancePickerProps {
  selectedInsuranceProvider: CheckoutInsuranceProvider | null;
  selectedInsurancePlan: CheckoutInsurancePlan | null;
  onInsuranceSelect: (insurance: InsuranceData) => void;
  formValidity: ValidationState;
}

export const InsurancePicker = ({
  selectedInsuranceProvider,
  selectedInsurancePlan,
  onInsuranceSelect,
  formValidity,
}: InsurancePickerProps) => {
  const clinicCountry = useSelector(selectDataClinicCountry);
  const isClinicUS = clinicCountry === COUNTRY.unitedStates;

  const insuranceList = useSelector(selectInsuranceListForCountry);

  // Take selected provider and plan and convert to InsuranceData
  const selectedInsurance = useMemo<InsuranceData>(() => ({
    providerId: selectedInsuranceProvider?.id,
    provider: selectedInsuranceProvider?.name,
    planId: selectedInsurancePlan?.id,
    plan: selectedInsurancePlan?.name,
  }), [selectedInsuranceProvider, selectedInsurancePlan]);

  const [CarrierSelect, PlanSelect] = (() => {
    const selectComponents = InsuranceSelect({
      insuranceProvidersWithPlans: insuranceList,
      onInsuranceChange: onInsuranceSelect,
      initialInsurance: selectedInsurance,
      returnSplitComponents: true,
    });

    if (Array.isArray(selectComponents) && selectComponents.length === 2) {
      return selectComponents;
    }

    Logger.error('InsuranceSelect did not return split components as expected.');
    return [<Fragment key="empty-carrier" />, <Fragment key="empty-plan" />];
  })();

  const providerErrorMessage = formValidity.insuranceProvider?.message;
  const isProviderInvalid = Boolean(providerErrorMessage);

  const planErrorMessage = formValidity.insurancePlan?.message;
  const isPlanInvalid = Boolean(planErrorMessage);

  return (
    <>
      <LabeledInput label="Insurance Carrier">
        <SelectWrapper $isInvalid={isProviderInvalid}>
          {CarrierSelect}
          {providerErrorMessage && <ErrorMessage>{providerErrorMessage}</ErrorMessage>}
        </SelectWrapper>
      </LabeledInput>

      {isClinicUS && (
        <LabeledInput label="Insurance Plan">
          <SelectWrapper $isInvalid={isPlanInvalid}>
            {PlanSelect}
            {planErrorMessage && <ErrorMessage>{planErrorMessage}</ErrorMessage>}
          </SelectWrapper>
        </LabeledInput>
      )}
    </>
  );
};
